import type { TableMenuItem } from '@/components_deprecated'
import { Card, QuillRenderer } from '@/components_deprecated'
import { PopupMenu } from '@/components_deprecated/PopupMenu'
import type { ForumQuillEditorRef } from '@/modules/forum/components'
import { ForumQuillEditor } from '@/modules/forum/components'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import type { VehicleNote } from '@/modules/vehicles/types.graphql'
import {
  format,
  PERMISSIONS,
  removeCurrentYear,
  safeParseJson,
  useAdmin,
  useConfirm,
  usePermissions,
} from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import type Quill from 'quill'
import type { Op } from 'quill-delta'
import Delta from 'quill-delta'
import { useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import styled, { css } from 'styled-components'
import { useVehicleNoteMutations } from '../mutations.hooks'

const Wrapper = styled(Card)`
  padding: 0;

  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1rem 1rem 1.5rem !important;
    border-bottom: 1px solid ${props => props.theme.colors.gray8};
  }

  ${props => props.theme.media.mobile} {
    header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`

const PostInfo = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 0.8rem;

  .thumb {
    grid-column: 1;
    grid-row: span 2;
  }

  a {
    grid-column: 2;
    grid-row: 1;
    color: ${props => props.theme.colors.primary};
  }

  span.date {
    grid-column: 2;
    grid-row: 2;
    color: ${props => props.theme.colors.gray5};
  }

  & > * {
    align-self: center;
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: auto 1fr auto;
    width: 100%;
  }
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 1rem;
  }

  ${props => props.theme.media.mobile} {
    justify-content: center;
    width: 100%;
    margin-top: 1rem;

    & > * + * {
      margin-left: 0;
    }
  }
`

interface ContentProps {
  editing: boolean
}

const Content = styled.div<ContentProps>`
  padding: ${props => !props.editing && '1.5rem'};

  ${props =>
    props.editing &&
    css`
      border-bottom-left-radius: ${props.theme.sizes.defaultBorderRadius};
      border-bottom-right-radius: ${props.theme.sizes.defaultBorderRadius};
    `};
`

interface Props {
  note: VehicleNote
}

export const VehicleNoteInfo: React.FC<Props> = ({ note }) => {
  const translations = useTranslate({
    writtenWhen: ['forum.written-when', { when: '' }],
    edit: 'common.edit',
    cancelEdit: 'common.cancel-changes',
    delete: 'common.delete-alt',

    updateNote: 'vehicles.update-note',

    prompt: {
      deleteTitle: 'vehicles.delete-vehicle-note',
      deleteMessage: 'vehicles.delete-vehicle-note-message',
    },
  })
  const [delta, setDelta] = useState(
    new Delta(safeParseJson<Op[]>(note.text, []))
  )
  const [editing, setEditing] = useState<boolean>(false)

  const quillRef = useRef<ForumQuillEditorRef>(null)
  const admin = useAdmin()
  const confirm = useConfirm()

  const { hasPermissionsAndMe } = usePermissions()
  const { patchNote, deleteNote, loading } = useVehicleNoteMutations()

  const canEdit =
    admin ||
    !!hasPermissionsAndMe(note.user, PERMISSIONS.vehicles.change.vehiclenote)
  const canDelete =
    admin ||
    !!hasPermissionsAndMe(note.user, PERMISSIONS.vehicles.delete.vehiclenote)
  const showMenu = canEdit || canDelete

  const onInitQuill = (quill: Quill) => {
    quill.setContents(delta)
    quill.setSelection(quill.getLength(), 0)
  }

  const onEditSubmit = async (text: string) => {
    try {
      await patchNote({
        variables: {
          id: note.id,
          input: {
            text: text,
          },
        },
      })
      setDelta(new Delta(safeParseJson<Op[]>(text, [])))
    } finally {
      setEditing(false)
    }
  }

  async function handleDelete() {
    if (!canDelete) return

    const { data: answer } = await confirm(
      translations.prompt.deleteMessage,
      translations.prompt.deleteTitle
    )
    if (!answer) return

    await deleteNote({
      variables: {
        id: note.id,
      },
    })
  }

  const menuItems: TableMenuItem<never>[] = (() => {
    if (!showMenu) return []

    const items: TableMenuItem<never>[] = []

    if (canEdit)
      items.push({
        label: editing ? translations.cancelEdit : translations.edit,
        onClick: () => setEditing(!editing),
      })
    if (canDelete)
      items.push({
        label: translations.delete,
        onClick: handleDelete,
      })

    return items
  })()

  return (
    <Wrapper>
      <header>
        <PostInfo>
          <UserProfileLink user={note.user} />
          <span className="date">
            {translations.writtenWhen({
              when: removeCurrentYear(
                format(parseISO(note.createdAt), 'PPP HH:mm')
              ),
            })}
          </span>

          {isMobile && showMenu && (
            <PopupMenu items={menuItems} loading={loading} />
          )}
        </PostInfo>

        <HeaderRight>
          {!isMobile && showMenu && (
            <PopupMenu items={menuItems} loading={loading} />
          )}
        </HeaderRight>
      </header>
      <Content editing={editing}>
        {editing ? (
          <ForumQuillEditor
            ref={quillRef}
            initialContent={note.text}
            roundedTopCorners
            submitText={translations.updateNote}
            noAttachments={false}
            onInit={onInitQuill}
            onSubmit={onEditSubmit}
          />
        ) : (
          <QuillRenderer contents={delta} />
        )}
      </Content>
    </Wrapper>
  )
}
